import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Building2,
  CheckCircle,
  Loader2,
  LogIn,
  Plus,
  RefreshCw,
  Search,
} from 'lucide-react';
import { api, extractErrorMessage } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { CreateTenantModal } from '../../components/platform/CreateTenantModal';

/**
 * Page super-admin `/platform/tenants` (US-TR-10 + US-TR-13).
 *
 *  - Liste des tenants (code / nom / statut)
 *  - Recherche texte + filtre statut
 *  - Action "Impersonifier" : appelle `useAuth().impersonateTenant(id)`
 *    qui POST `/platform/tenants/:id/impersonate`, stocke le nouveau
 *    access token, puis redirige vers `/dashboard` en vue tenant.
 *
 * Réservée aux super-admin plateforme (filtre côté UI + côté API).
 */

interface TenantRow {
  id: string;
  code: string;
  nom: string;
  sous_domaine: string | null;
  statut: 'actif' | 'suspendu' | 'desactive' | 'en_demonstration';
  contact_principal: unknown;
  date_creation: string;
}

const STATUTS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Tous les statuts' },
  { value: 'actif', label: 'Actif' },
  { value: 'suspendu', label: 'Suspendu' },
  { value: 'desactive', label: 'Désactivé' },
  { value: 'en_demonstration', label: 'En démonstration' },
];

export function TenantsPage() {
  const { impersonateTenant } = useAuth();
  const { isPlatformAdmin } = usePermissions();
  const navigate = useNavigate();

  const [items, setItems] = useState<TenantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statut, setStatut] = useState('');
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  async function refresh() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (statut) params.set('statut', statut);
      const res = await api.get<{ data: TenantRow[] }>(
        `/platform/tenants${params.toString() ? `?${params}` : ''}`,
      );
      setItems(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isPlatformAdmin) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, statut, isPlatformAdmin]);

  async function handleImpersonate(t: TenantRow) {
    if (t.statut !== 'actif') {
      setErrorMessage(`Le tenant ${t.code} n'est pas actif — impersonation impossible.`);
      return;
    }
    setImpersonatingId(t.id);
    setErrorMessage(null);
    try {
      await impersonateTenant(t.id);
      // Soft navigation : conserve l'access_token d'impersonation en mémoire.
      // Un hard reload ré-authentifierait via le cookie refresh (super-admin)
      // et perdrait la session impersonation.
      navigate('/dashboard');
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
      setImpersonatingId(null);
    }
  }

  if (!isPlatformAdmin) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <p className="text-sm text-gray-500">
          Cette page est réservée aux administrateurs plateforme.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Building2 className="w-7 h-7 mr-2 text-primary-600" />
            Tenants
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Gestion des organisations clientes — {items.length} tenant(s).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            className="inline-flex items-center p-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
            title="Rafraîchir"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setCreateModalOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 shadow-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau tenant
          </button>
        </div>
      </div>

      {createModalOpen && (
        <CreateTenantModal onClose={() => setCreateModalOpen(false)} onCreated={refresh} />
      )}

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <select
            value={statut}
            onChange={(e) => setStatut(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {STATUTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher (code / nom)…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg w-64 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom
                </th>
                <th className="hidden md:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sous-domaine
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="hidden lg:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Créé le
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-500 mx-auto" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-500">
                    Aucun tenant trouvé.
                  </td>
                </tr>
              ) : (
                items.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2.5 text-xs font-semibold text-primary-600 uppercase tracking-wide whitespace-nowrap">
                      {t.code}
                    </td>
                    <td className="px-3 py-2.5 text-gray-900">{t.nom}</td>
                    <td className="hidden md:table-cell px-3 py-2.5 text-gray-600 font-mono text-xs">
                      {t.sous_domaine ?? '—'}
                    </td>
                    <td className="px-3 py-2.5">
                      <StatutBadge statut={t.statut} />
                    </td>
                    <td className="hidden lg:table-cell px-3 py-2.5 text-gray-500 text-xs whitespace-nowrap">
                      {new Date(t.date_creation).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => handleImpersonate(t)}
                        disabled={impersonatingId === t.id || t.statut !== 'actif'}
                        className="inline-flex items-center px-2.5 py-1.5 border border-primary-300 rounded-lg text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 disabled:opacity-50"
                        title={
                          t.statut !== 'actif'
                            ? 'Tenant non actif'
                            : 'Se connecter en tant que ce tenant'
                        }
                      >
                        {impersonatingId === t.id ? (
                          <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                        ) : (
                          <LogIn className="w-3.5 h-3.5 mr-1" />
                        )}
                        Impersonifier
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatutBadge({ statut }: { statut: TenantRow['statut'] }) {
  const cfg: Record<TenantRow['statut'], { cls: string; label: string }> = {
    actif: {
      cls: 'bg-green-100 text-green-800 border-green-200',
      label: 'Actif',
    },
    suspendu: {
      cls: 'bg-amber-100 text-amber-800 border-amber-200',
      label: 'Suspendu',
    },
    desactive: {
      cls: 'bg-gray-100 text-gray-700 border-gray-200',
      label: 'Désactivé',
    },
    en_demonstration: {
      cls: 'bg-blue-100 text-blue-800 border-blue-200',
      label: 'Démo',
    },
  };
  const c = cfg[statut];
  return (
    <span
      className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium border ${c.cls}`}
    >
      {statut === 'actif' && <CheckCircle className="w-3 h-3 mr-1" />}
      {c.label}
    </span>
  );
}
